/**
 * What the backend's LLM articulation pass changed.
 *
 * Compile posts the reviewed clinical summary to validate_discharge_payload/,
 * which returns prose. That result used to be applied straight over the
 * doctor's own data, so the sentences on a document they signed were never
 * shown to them as a change — they reviewed raw clinical facts, an LLM rewrote
 * them, and the rewrite was only ever visible as HTML they could not trace back
 * to fields.
 *
 * This produces the field-level before/after the Compile dialog puts in front
 * of them.
 */

// Keys we attach to the payload ourselves. They are not clinical content and
// must never show up as "a change the AI made".
const INTERNAL_KEYS = new Set([
  "html_content",
  "htmlContent",
  "__template_id",
  "admission_id",
  "pdf_url",
]);

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

// A medication is rendered with the fields that make it a different
// instruction. Rendering by name alone would hide an articulated dose or
// frequency change — precisely the edit a doctor must not miss.
const renderItem = (v) => {
  if (!isPlainObject(v)) return v;
  const name = v.name || v.drug_name;
  if (!name) {
    try {
      return JSON.stringify(v);
    } catch {
      return "";
    }
  }
  return [name, v.dose, v.frequency, v.period && `${v.period} ${v.periodUnit || ""}`.trim()]
    .filter(Boolean)
    .join(" ");
};

const render = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map(renderItem)
      .filter((v) => v !== null && v !== undefined && v !== "")
      .join(", ");
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isPlainObject(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value);
};

const toLabel = (path) =>
  path
    .split(".")
    .map((part) => part.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(" › ");

// Walk both payloads together so added and removed fields are both reported.
// Arrays are leaves: an articulated list is one editorial change, not N.
const walk = (before, after, prefix, out) => {
  const keys = new Set([
    ...(isPlainObject(before) ? Object.keys(before) : []),
    ...(isPlainObject(after) ? Object.keys(after) : []),
  ]);

  [...keys].sort().forEach((key) => {
    if (INTERNAL_KEYS.has(key)) return;
    const path = prefix ? `${prefix}.${key}` : key;
    const b = isPlainObject(before) ? before[key] : undefined;
    const a = isPlainObject(after) ? after[key] : undefined;

    if (isPlainObject(b) || isPlainObject(a)) {
      walk(b, a, path, out);
      return;
    }

    const bt = render(b);
    const at = render(a);
    if (bt !== at) out.push({ path, label: toLabel(path), before: bt, after: at });
  });
};

export const computeArticulationDiff = (before, after) => {
  const out = [];
  walk(before, after, "", out);
  return out;
};

export default computeArticulationDiff;
